import { useEffect, useRef } from 'react';
import { registerLiveRegion } from '@/lib/announce';

/**
 * The shell's one live region — `FE-04.11`, `entry-surfaces.md` D11.
 *
 * `<div aria-live="polite" aria-atomic="true" class="sr-only">`, verbatim.
 * Never `assertive`: nothing this shell announces is an emergency, and an
 * assertive region interrupts a member mid-sentence.
 *
 * This is a **shell singleton**: `RootLayout` (`routes/__root.tsx`) mounts
 * it exactly once, before `<main>` — a screen reader's virtual cursor must
 * not land inside a region that can mutate under it. It owns the only DOM
 * node `announce()` ever writes to; a second `<LiveRegion>` anywhere in the
 * tree (or a component that rolls its own `role="status"` node instead of
 * calling `announce()`) is the bug D11 names by name: one of the two
 * regions silently stops being read. That is why this component carries no
 * props and is not exported from the primitives barrel — it is wired once,
 * here, and nowhere else imports it.
 */
export function LiveRegion() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    registerLiveRegion(ref.current);
    return () => registerLiveRegion(null);
  }, []);

  return <div ref={ref} aria-live="polite" aria-atomic="true" className="sr-only" />;
}
